import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Clock, MapPin, ArrowLeft, ExternalLink, MessageSquare, Phone, Mail } from "lucide-react";
import { Seo } from "../components/Seo";

function StarRating({ value, onChange, size = "w-5 h-5", interactive = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!interactive}
          data-testid={interactive ? `star-${n}` : undefined}
          onMouseEnter={() => interactive && setHover(n)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onChange(n)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
        >
          <Star className={`${size} ${(hover || value) >= n ? "fill-[#F4A261] text-[#F4A261]" : "text-[#D9D2C7]"}`} />
        </button>
      ))}
    </div>
  );
}

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customer } = useAuth();
  const { t } = useLang();
  const [rest, setRest] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = useCallback(async () => {
    const r = await api.get(`/restaurants/${id}/reviews`);
    setReviews(r.data);
  }, [id]);

  const loadRestaurant = useCallback(async () => {
    const r = await api.get(`/restaurants/${id}`);
    setRest(r.data);
  }, [id]);

  useEffect(() => {
    Promise.all([loadRestaurant(), loadReviews()])
      .catch(() => setRest(null))
      .finally(() => setLoading(false));
  }, [loadRestaurant, loadReviews]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error(t("toast.chooseRating")); return; }
    setSubmitting(true);
    try {
      await api.post(`/restaurants/${id}/reviews`, { rating, comment });
      toast.success(t("toast.thanksReview"));
      setRating(0); setComment("");
      await Promise.all([loadReviews(), loadRestaurant()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toast.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]"><Header />
        <div className="max-w-5xl mx-auto px-6 py-12"><div className="h-96 rounded-2xl bg-[#F4EFE6] animate-pulse" /></div>
      </div>
    );
  }
  if (!rest) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]"><Header />
        <p className="text-center py-20 font-mono text-[#5C5C5C]">{t("detail.notFound")}</p>
      </div>
    );
  }

  const categories = [...new Set((rest.menu || []).map((m) => m.category || "Plats"))];
  const orderUrl = rest.ubereats_link || rest.doordash_link || rest.order_link;
  const handleOrder = () => {
    if (orderUrl) window.open(orderUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        path={`/restaurant/${rest.id}`}
        title={`${rest.name} — ${rest.cuisine || "Restaurant"} au Québec | Vytali`}
        description={
          rest.description
            ? `${rest.description.slice(0, 155)}`
            : `Découvrez ${rest.name}, ${rest.cuisine || "restaurant"} partenaire Vytali au Québec. Commandez en ligne avec 5% de cashback membre.`
        }
        keywords={`${rest.name}, ${rest.cuisine || ""}, ${rest.city || "Québec"}, restaurant, livraison, commande en ligne`}
        image={rest.image}
        type="article"
      />
      <Header />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <button data-testid="back-btn" onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-bold text-[#5C5C5C] hover:text-[#2B2B2B] transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> {t("detail.back")}
        </button>

        <motion.button
          type="button" onClick={handleOrder}
          data-testid="detail-image-order"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="group relative block w-full rounded-2xl overflow-hidden border border-[#E5E0D8] aspect-[16/7] mb-8 cursor-pointer">
          <img src={rest.image || "https://images.unsplash.com/photo-1667388968964-4aa652df0a9b?w=1200"} alt={rest.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          {orderUrl && (
            <span className="absolute bottom-4 right-4 flex items-center gap-1 bg-[#E63946] text-white text-sm font-bold px-4 py-2 rounded-full">
              {t("detail.orderOnline")} <ExternalLink className="w-4 h-4" />
            </span>
          )}
        </motion.button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-xs font-mono bg-[#F4EFE6] text-[#2B2B2B] px-3 py-1 rounded-full">{rest.cuisine}</span>
            <h1 className="mt-3 text-4xl md:text-5xl font-display font-black tracking-tighter text-[#2B2B2B]">{rest.name}</h1>
            <p className="mt-3 text-[#5C5C5C] max-w-2xl leading-relaxed">{rest.description}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm font-mono text-[#5C5C5C]">
              <span className="flex items-center gap-1 text-[#4A5D23] font-bold"><Star className="w-4 h-4 fill-[#F4A261] text-[#F4A261]" /> {rest.rating || "—"} {rest.review_count ? `(${rest.review_count})` : ""}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {rest.delivery_time}</span>
              <span>{rest.price_range}</span>
              {rest.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {rest.city}</span>}
              {rest.phone && <a href={`tel:${rest.phone}`} data-testid="detail-phone" className="flex items-center gap-1 hover:text-[#E63946] transition-colors"><Phone className="w-4 h-4" /> {rest.phone}</a>}
              {rest.email && <a href={`mailto:${rest.email}`} data-testid="detail-email" className="flex items-center gap-1 hover:text-[#E63946] transition-colors"><Mail className="w-4 h-4" /> {rest.email}</a>}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button data-testid="order-online-btn" onClick={handleOrder} disabled={!orderUrl}
              className="flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-40 disabled:cursor-not-allowed">
              {t("detail.orderOnline")} <ExternalLink className="w-4 h-4" />
            </button>
            <div className="flex gap-2">
              {rest.doordash_link && (
                <a data-testid="detail-doordash-btn" href={rest.doordash_link} target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 border-[#EB1700] text-[#EB1700] font-bold text-sm hover:bg-[#FFF3F1] transition-colors">
                  DoorDash <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {rest.ubereats_link && (
                <a data-testid="detail-ubereats-btn" href={rest.ubereats_link} target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 border-[#06C167] text-[#05893f] font-bold text-sm hover:bg-[#F0FBF5] transition-colors">
                  Uber Eats <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="mt-12">
          <h2 className="text-3xl font-display font-bold tracking-tight text-[#2B2B2B] mb-6">{t("detail.menu")}</h2>
          {(!rest.menu || rest.menu.length === 0) ? (
            <p className="font-mono text-sm text-[#5C5C5C]">{t("detail.menuSoon")}</p>
          ) : (
            categories.map((cat) => (
              <div key={cat} className="mb-8">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C5C5C] font-mono mb-4">{cat}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rest.menu.filter((m) => (m.category || "Plats") === cat).map((m) => (
                    <div key={m.id || m.name} data-testid={`menu-item-${m.id || m.name}`} className="flex items-start justify-between gap-4 bg-white border border-[#E5E0D8] rounded-xl p-4">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-bold text-[#2B2B2B]">{m.name}</h4>
                        {m.description && <p className="text-sm text-[#5C5C5C] mt-0.5">{m.description}</p>}
                        <span className="font-mono font-bold text-[#E63946] inline-block mt-2">{Number(m.price).toFixed(2)} $</span>
                      </div>
                      {m.image ? (
                        <img
                          src={m.image}
                          alt={m.name}
                          data-testid={`menu-item-image-${m.id || m.name}`}
                          className="w-24 h-24 rounded-lg object-cover shrink-0 border border-[#E5E0D8]"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reviews */}
        <div className="mt-14 border-t border-[#E5E0D8] pt-10">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-6 h-6 text-[#E63946]" />
            <h2 className="text-3xl font-display font-bold tracking-tight text-[#2B2B2B]">
              {t("detail.reviews")} {reviews.length > 0 && <span className="text-[#5C5C5C] font-mono text-xl">({reviews.length})</span>}
            </h2>
          </div>

          {/* Add review */}
          {customer ? (
            <form data-testid="review-form" onSubmit={submitReview} className="bg-white border border-[#E5E0D8] rounded-xl p-6 mb-8">
              <p className="text-sm font-bold text-[#2B2B2B] mb-3">{t("detail.yourRating")}</p>
              <StarRating value={rating} onChange={setRating} interactive size="w-8 h-8" />
              <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder={t("detail.reviewPlaceholder")}
                className="mt-4 w-full h-24 p-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors resize-none text-sm" />
              <button data-testid="submit-review-btn" type="submit" disabled={submitting}
                className="mt-4 px-6 py-3 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50">
                {submitting ? "…" : t("detail.publishReview")}
              </button>
            </form>
          ) : (
            <div data-testid="review-login-prompt" className="bg-[#F4EFE6] border border-[#E5E0D8] rounded-xl p-6 mb-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-[#5C5C5C]">{t("detail.loginToReview")}</p>
              <button onClick={() => navigate("/login")} className="px-5 py-2.5 rounded-full bg-[#2B2B2B] text-white font-bold text-sm hover:scale-[1.02] transition-transform">
                {t("detail.signIn")}
              </button>
            </div>
          )}

          {/* Review list */}
          {reviews.length === 0 ? (
            <p data-testid="no-reviews" className="font-mono text-sm text-[#5C5C5C]">{t("detail.noReviews")}</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((rv) => (
                <div key={rv.id} data-testid={`review-${rv.id}`} className="bg-white border border-[#E5E0D8] rounded-xl p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {rv.user_picture ? (
                        <img src={rv.user_picture} alt={rv.user_name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <span className="w-9 h-9 rounded-full bg-[#F4A261] flex items-center justify-center text-white font-bold">
                          {(rv.user_name || "C")[0].toUpperCase()}
                        </span>
                      )}
                      <span className="font-bold text-[#2B2B2B]">{rv.user_name}</span>
                    </div>
                    <StarRating value={rv.rating} size="w-4 h-4" />
                  </div>
                  {rv.comment && <p className="mt-3 text-[#5C5C5C] leading-relaxed">{rv.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
